import { ExpressionSpecification, LayerSpecification } from "mapbox-gl";

import GaiaMapStyle from "~/modules/map/components/GaiaMapStyle";

import { LABEL_CONFIGS, LabelColor } from "./layerConfigs";
import { LAYER_DEFAULTS } from "./layerDefaults";

interface GaiaMapStyleCircleProps {
    id: string;
    source: string;
    radius?: number;
    color?: ExpressionSpecification | string;
    strokeWidth?: number;
    strokeColor?: ExpressionSpecification | string;
    icon?: {
        image: ExpressionSpecification | string;
        color: ExpressionSpecification | string;
        size?: number;
        layout?: Record<string, any>;
    };
    label?: {
        textField: ExpressionSpecification | string;
        color?: LabelColor;
        filter?: ExpressionSpecification;
        layout?: Record<string, any>;
        paint?: Record<string, any>;
    };
    badge?: {
        countField: ExpressionSpecification | string;
        minCount?: number;
        filter?: ExpressionSpecification;
    };
    draggable?: {
        image?: string;
        opacity?: number;
        filter?: ExpressionSpecification;
    };
    filter?: ExpressionSpecification;
    paint?: Record<string, any>;
}

export function GaiaMapStyleCircle({
    id,
    source,
    radius = LAYER_DEFAULTS.circle.radius,
    color = LAYER_DEFAULTS.circle.color,
    strokeWidth,
    strokeColor,
    icon,
    label,
    badge,
    draggable,
    filter,
    paint,
}: GaiaMapStyleCircleProps) {
    const layers = [
        createBaseCircleLayer(id, source, radius, color, strokeWidth, strokeColor, filter, paint),
        icon && createIconLayer(id, source, icon, filter),
        label && createLabelLayer(id, source, label),
        draggable && createDraggableIndicatorLayer(id, source, draggable),
        badge && createBadgeLayer(id, source, badge, filter),
    ].filter((layer): layer is LayerSpecification => !!layer);

    return <GaiaMapStyle layers={layers} />;
}

function createBaseCircleLayer(
    id: string,
    source: string,
    radius: number,
    color: ExpressionSpecification | string,
    strokeWidth: number | undefined,
    strokeColor: ExpressionSpecification | string | undefined,
    filter: ExpressionSpecification | undefined,
    paint: Record<string, any> | undefined,
): LayerSpecification {
    return {
        id: id,
        source,
        type: "circle",
        slot: LAYER_DEFAULTS.slot,
        ...(filter && { filter }),
        paint: {
            "circle-radius": radius,
            "circle-color": color,
            ...(strokeWidth && { "circle-stroke-width": strokeWidth }),
            ...(strokeColor && { "circle-stroke-color": strokeColor }),
            ...paint,
        },
    };
}

function createIconLayer(
    id: string,
    source: string,
    icon: {
        image: ExpressionSpecification | string;
        color: ExpressionSpecification | string;
        size?: number;
        layout?: Record<string, any>;
    },
    filter: ExpressionSpecification | undefined,
): LayerSpecification {
    return {
        id: `${id}_icon`,
        source,
        type: "symbol",
        slot: LAYER_DEFAULTS.slot,
        ...(filter && { filter }),
        layout: {
            ...LAYER_DEFAULTS.symbolLayout,
            "icon-image": icon.image,
            "icon-size": icon.size ?? 0.5,
            ...icon.layout,
        },
        paint: {
            "icon-color": icon.color,
        },
    };
}

function createLabelLayer(
    id: string,
    source: string,
    label: {
        textField: ExpressionSpecification | string;
        color?: LabelColor;
        filter?: ExpressionSpecification;
        layout?: Record<string, any>;
        paint?: Record<string, any>;
    },
): LayerSpecification {
    const color = label.color ?? "assigned";
    const config = LABEL_CONFIGS[color];

    return {
        id: `${id}_label`,
        source,
        type: "symbol",
        slot: LAYER_DEFAULTS.slot,
        ...(label.filter && { filter: label.filter }),
        layout: {
            ...LAYER_DEFAULTS.symbolLayout,
            ...LAYER_DEFAULTS.textLayout,
            "icon-image": `label-bg-${color}`,
            "text-field": label.textField,
            "text-offset": config.textOffset,
            "text-anchor": config.anchor,
            ...(label.layout && label.layout),
        },
        paint: {
            ...LAYER_DEFAULTS.textPaint,
            ...label.paint,
        },
    };
}

function createBadgeLayer(
    id: string,
    source: string,
    badge: {
        countField: ExpressionSpecification | string;
        minCount?: number;
        filter?: ExpressionSpecification;
    },
    parentFilter: ExpressionSpecification | undefined,
): LayerSpecification {
    const badgeFilter =
        badge.filter ??
        (parentFilter
            ? ["all", parentFilter, [">=", badge.countField, badge.minCount ?? 2]]
            : [">=", badge.countField, badge.minCount ?? 2]);

    return {
        id: `${id}_badge`,
        source,
        type: "symbol",
        slot: LAYER_DEFAULTS.slot,
        ...(badgeFilter && { filter: badgeFilter }),
        layout: {
            "icon-image": "label-bg-assigned",
            "icon-text-fit": "both",
            "icon-text-fit-padding": [0, -2, 0, -2] as [number, number, number, number],
            "text-field": badge.countField,
            "text-font": LAYER_DEFAULTS.textLayout["text-font"],
            "text-size": 13,
            "text-offset": [1, -2] as [number, number],
            "text-anchor": "top",
            "icon-allow-overlap": true,
            "text-allow-overlap": true,
        },
        paint: {
            "text-color": "#ffffff",
        },
    };
}

function createDraggableIndicatorLayer(
    id: string,
    source: string,
    draggable: {
        image?: string;
        opacity?: number;
        filter?: ExpressionSpecification;
    },
): LayerSpecification {
    return {
        id: `${id}_draggable`,
        source,
        type: "symbol",
        slot: LAYER_DEFAULTS.slot,
        ...(draggable.filter && { filter: draggable.filter }),
        layout: {
            "icon-image": draggable.image ?? "markerCircleDashedStroke",
            "icon-allow-overlap": true,
            "icon-size": 0.5,
        },
        paint: {
            "icon-color": LAYER_DEFAULTS.iconColor.draggableIndicator,
            "icon-opacity": draggable.opacity ?? 0.4,
        },
    };
}
